import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { getLang } from "@/lib/i18n";

const EMPTY = { name: "", email: "", password: "", edition_slug: "", role: "learner", track: "" };

const CreateUser = ({ editions, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [show, setShow] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", { ...f, edition_slug: f.edition_slug || null, track: f.track || null });
      toast.success(`Access granted to ${f.email}`);
      setF(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-create-user-form" className="grid gap-3 border border-border bg-card p-5 md:grid-cols-6">
      <Input data-testid="new-user-name" required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="md:col-span-2" />
      <Input data-testid="new-user-email" required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="md:col-span-2" />
      <div className="relative md:col-span-2">
        <Input data-testid="new-user-password" required minLength={8} type={show ? "text" : "password"} placeholder="Temporary password (8+)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="pr-10" />
        <button type="button" aria-label={show ? "Hide password" : "Show password"} data-testid="new-user-password-toggle" onClick={() => setShow(!show)} className="absolute right-0 top-0 flex h-10 w-10 items-center justify-center text-muted-foreground">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
      </div>
      <select data-testid="new-user-edition" value={f.edition_slug} onChange={(e) => setF({ ...f, edition_slug: e.target.value })} className="h-10 rounded-md border border-input bg-background px-2 text-sm md:col-span-2">
        <option value="">Default brand</option>
        {editions.map((ed) => <option key={ed.slug} value={ed.slug}>{ed.brand_name}</option>)}
      </select>
      <select data-testid="new-user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="h-10 rounded-md border border-input bg-background px-2 text-sm md:col-span-2">
        <option value="learner">Learner</option><option value="admin">Admin</option>
      </select>
      <select data-testid="new-user-track" value={f.track} onChange={(e) => setF({ ...f, track: e.target.value })} className="h-10 rounded-md border border-input bg-background px-2 text-sm md:col-span-2">
        <option value="">Track: user chooses at first sign-in</option><option value="executive">Executive Briefing</option><option value="strategist">Full programme</option>
      </select>
      <button data-testid="new-user-submit" className="h-10 rounded-sm bg-brand-primary text-sm font-semibold text-brand-primary-fg md:col-span-2">Grant access</button>
    </form>
  );
};

export const UsersTab = () => {
  const qc = useQueryClient();
  const [focus, setFocus] = useState(null);
  const { data: users } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const { data: editions = [] } = useQuery({ queryKey: ["admin-editions"], queryFn: async () => (await api.get("/admin/editions")).data });
  const { data: log = [] } = useQuery({ queryKey: ["admin-activity", focus], queryFn: async () => (await api.get("/admin/activity", { params: focus ? { user_id: focus } : {} })).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-activity"] }); };
  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success(msg);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const reset = (u) => {
    const pw = window.prompt(`New password for ${u.email} (8+ characters)`);
    if (pw) patch(u.id, { password: pw }, "Password reset");
  };
  const sendAccess = async (u) => {
    if (!window.confirm(`Email ${u.email} a link to set their password? Any earlier link stops working.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/send-access`);
      toast.success(`Access email sent to ${u.email}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="space-y-8">
      <CreateUser editions={editions} onCreated={refresh} />
      <div className="overflow-x-auto border border-border bg-card">
        <table data-testid="admin-users-table" className="w-full text-left text-sm">
          <thead><tr className="border-b border-border font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{["User", "Role", "Track", "Brand", "Tier", "Last login", "Logins", "Access", ""].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
          <tbody>
            {(users || []).map((u) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.email}`} className={`border-b border-border ${focus === u.id ? "bg-muted/60" : ""}`}>
                <td translate="no" className="p-3"><button type="button" onClick={() => setFocus(focus === u.id ? null : u.id)} className="text-left"><div className="font-semibold">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div>{u.must_change_password && <div translate="yes" data-testid={`user-temp-pw-${u.email}`} className="mt-1 text-[11px] font-semibold text-amber-600">Temporary password · change pending</div>}</button></td>
                <td className="p-3">{u.role}</td><td className="p-3">{u.track || "—"}</td><td className="p-3">{u.edition_slug}</td><td className="p-3">{u.current_tier}</td>
                <td className="p-3 text-xs">{u.last_login ? new Date(u.last_login).toLocaleString(getLang()) : "—"}</td><td className="p-3 font-mono">{u.login_count}</td>
                <td className="p-3"><Switch data-testid={`user-active-${u.email}`} checked={u.active} onCheckedChange={(c) => patch(u.id, { active: c }, c ? "Access enabled" : "Access disabled")} /></td>
                <td className="p-3"><div className="flex flex-col items-start gap-1.5"><button type="button" data-testid={`user-send-access-${u.email}`} onClick={() => sendAccess(u)} disabled={!u.active} className="text-xs font-semibold text-brand-primary disabled:opacity-40">Send access email</button>{u.access_email_sent_at && <span data-testid={`user-access-sent-${u.email}`} className="text-[11px] text-muted-foreground">Sent {new Date(u.access_email_sent_at).toLocaleString(getLang())}</span>}<button type="button" data-testid={`user-reset-${u.email}`} onClick={() => reset(u)} className="text-xs font-semibold text-brand-primary">Reset password</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <div className="overline-label mb-3">Activity log {focus ? "· selected user" : "· all users"} (latest 300)</div>
        <div data-testid="admin-activity-log" className="max-h-96 overflow-auto border border-border bg-card text-xs">
          {log.map((a, i) => <div key={i} className="flex flex-wrap gap-3 border-b border-border px-3 py-2"><span className="font-mono text-muted-foreground">{new Date(a.at).toLocaleString(getLang())}</span><span className="font-semibold">{a.email}</span><span className="text-brand-primary">{a.type}</span><span translate="no" className="text-muted-foreground">{JSON.stringify(a.meta)}</span>{a.ip && <span className="text-muted-foreground">{a.ip}</span>}</div>)}
          {!log.length && <div className="p-4 text-muted-foreground">No activity yet.</div>}
        </div>
      </div>
    </div>
  );
};
